import Box from '@mui/material/Box';
import { Feature } from '../../../../models';

/**
 * The self-serve testing products of the plan page's "More testing plans" table - their rows,
 * names, units and product icons. Shared with the checkout's "Add products" / "Edit order" dialog
 * so both surfaces list the same catalogue (names, units, icons) without one importing the other.
 */

export type ProductIconKey = 'studio' | 'cloud' | 'local' | 'insights' | 'testmgmt';

const PRODUCT_ICON_META: Record<ProductIconKey, {
  backgroundColor: string;
  color: string;
}> = {
  studio: {
    backgroundColor: '#e3f6ee',
    color: '#12a877',
  },
  cloud: {
    backgroundColor: '#fdf3e1',
    color: '#bd862a',
  },
  local: {
    backgroundColor: '#ebecf7',
    color: '#5b5fb0',
  },
  insights: {
    backgroundColor: '#fce8f3',
    color: '#d6388a',
  },
  testmgmt: {
    backgroundColor: '#edeefb',
    color: '#7a7aef',
  },
};

const ProductGlyph = (
  { iconKey, color, backgroundColor }: {
    iconKey: ProductIconKey;
    color: string;
    backgroundColor: string;
  },
) => {
  const cutoutStrokeProps = {
    stroke: backgroundColor,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
  };

  switch (iconKey) {
    case 'studio':
      return (
        <>
          <rect x="2.5" y="4" width="19" height="13" rx="2.6" fill={color} />
          <rect x="10" y="17" width="4" height="2.4" rx="0" fill={color} />
          <rect x="7.5" y="19.2" width="9" height="1.8" rx="0.9" fill={color} />
          <path d="M9 8 6.7 10.5 9 13" fill="none" {...cutoutStrokeProps} strokeWidth={1.7} />
          <path d="M15 8 17.3 10.5 15 13" fill="none" {...cutoutStrokeProps} strokeWidth={1.7} />
        </>
      );
    case 'cloud':
      return (
        <path
          d="M7 19h9.2a4.3 4.3 0 0 0 .55-8.56 5.8 5.8 0 0 0-11.06-1.16A4.1 4.1 0 0 0 7 19z"
          fill={color}
        />
      );
    case 'local':
      return (
        <>
          <rect x="2.5" y="5" width="19" height="14" rx="2.6" fill={color} />
          <path d="M6.5 10.5 9 13l-2.5 2.5" fill="none" {...cutoutStrokeProps} />
          <path d="M12 15.2h5" fill="none" {...cutoutStrokeProps} />
        </>
      );
    case 'insights':
      return (
        <>
          <rect x="4.2" y="12" width="3.4" height="6.5" rx="1" fill={color} />
          <rect x="10.3" y="7.5" width="3.4" height="11" rx="1" fill={color} />
          <rect x="16.4" y="9.8" width="3.4" height="8.7" rx="1" fill={color} />
        </>
      );
    case 'testmgmt':
    default:
      return (
        <>
          <rect x="5" y="4.5" width="14" height="16" rx="3" fill={color} />
          <rect x="8.8" y="3" width="6.4" height="3.4" rx="1.3" fill={color} />
          <path d="M8.6 12.6l2.2 2.2 4.6-5" fill="none" {...cutoutStrokeProps} strokeWidth={2.1} />
        </>
      );
  }
};

export const ProductIconBadge = (
  {
    iconKey,
    size = 26,
    glyphSize = Math.round(size * 0.62),
    borderRadius = Math.round(size * 0.28),
  }: {
    iconKey: ProductIconKey;
    size?: number;
    glyphSize?: number;
    borderRadius?: number;
  },
) => {
  const meta = PRODUCT_ICON_META[iconKey];

  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        backgroundColor: meta.backgroundColor,
        // px string: numeric borderRadius in sx gets multiplied by theme.shape.borderRadius
        borderRadius: `${borderRadius}px`,
        display: 'inline-flex',
        flex: 'none',
        height: size,
        justifyContent: 'center',
        width: size,
      }}
    >
      <svg width={glyphSize} height={glyphSize} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
        <ProductGlyph
          iconKey={iconKey}
          color={meta.color}
          backgroundColor={meta.backgroundColor}
        />
      </svg>
    </Box>
  );
};

export type TrueTestingPlanOption = {
  type: 'product';
  feature: Feature;
  titleId: string;
  // Tooltip bullets: the product description first, then how its unit is consumed.
  infoIds: string[];
  // Unit shown under the price ("/ seat / yr").
  unit: 'seat' | 'license' | 'session';
  iconKey: ProductIconKey;
};

const SESSION_INFO_IDS = [
  'page.plan.v2.true.testing.info.session.one.test',
  'page.plan.v2.true.testing.info.session.parallel',
];

// KSE is not listed here — it renders as a row of the main plan table
// (KSE_STUDIO_PLAN_ROW) while keeping its "more testing products" state/preview path.
export const TRUE_TESTING_PLAN_OPTIONS: TrueTestingPlanOption[] = [
  // Self-serve TestOps G3 (Full licenses only — Guest/Collab stay Sales-managed). Rendered only
  // once its LD price key is seeded, see `testOpsRowEnabled`.
  {
    type: 'product',
    feature: Feature.TESTOPS_G3,
    titleId: 'page.plan.v2.true.testing.testops.title',
    infoIds: [
      'page.plan.v2.true.testing.testops.info',
      'page.plan.v2.true.testing.info.license.one.tester',
    ],
    unit: 'seat',
    iconKey: 'testmgmt',
  },
  {
    type: 'product',
    feature: Feature.UNLIMITED_ENGINE,
    titleId: 'feature.unlimited_engine.product.name',
    infoIds: [
      'page.plan.v2.true.testing.runtime.info',
      'page.plan.v2.true.testing.runtime.info.license',
    ],
    unit: 'license',
    iconKey: 'local',
  },
  {
    type: 'product',
    feature: Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
    titleId: 'page.plan.v2.true.testing.cross.browser.title',
    infoIds: [
      'page.plan.v2.true.testing.cross.browser.info',
      'page.plan.v2.true.testing.cross.browser.info.live',
      ...SESSION_INFO_IDS,
    ],
    unit: 'session',
    iconKey: 'cloud',
  },
  {
    type: 'product',
    feature: Feature.TESTCLOUD_SESSION_MOBILE_APP,
    titleId: 'page.plan.v2.true.testing.mobile.title',
    infoIds: [
      'page.plan.v2.true.testing.mobile.info',
      ...SESSION_INFO_IDS,
    ],
    unit: 'session',
    iconKey: 'cloud',
  },
  {
    type: 'product',
    feature: Feature.TESTCLOUD_SESSION_LIVE_TESTING,
    titleId: 'page.plan.v2.true.testing.live.testing.title',
    infoIds: [
      'page.plan.v2.true.testing.live.testing.info',
      'page.plan.v2.true.testing.live.testing.info.session',
      'page.plan.v2.true.testing.live.testing.info.no.automation',
    ],
    unit: 'session',
    iconKey: 'cloud',
  },
];

// Katalon Studio as a "More testing plans" row: only for an account that already owns a bundle,
// where the plan table has nothing else left to sell (no second Starter, TA/TP retired by R6).
export const KSE_TESTING_PLAN_OPTION: TrueTestingPlanOption = {
  type: 'product',
  feature: Feature.PER_USER_KSE,
  titleId: 'page.plan.v2.true.testing.studio.title',
  infoIds: [
    'page.plan.v2.true.testing.studio.info',
    'page.plan.v2.true.testing.info.license.one.tester',
  ],
  unit: 'license',
  iconKey: 'studio',
};
